import React from "react";
import { Linkedin, Instagram, Mail } from "lucide-react";
import SmartImage from "./SmartImage";
import Reveal from "./Reveal";
import { assetUrl } from "../adminApi";
import { useI18n } from "../i18n";

const socialsOf = (m) =>
  [
    m.linkedinEnabled && m.linkedin && { id: "linkedin", href: m.linkedin, Icon: Linkedin, label: "LinkedIn" },
    m.instagramEnabled && m.instagram && { id: "instagram", href: m.instagram, Icon: Instagram, label: "Instagram" },
    m.emailEnabled && m.email && { id: "email", href: `mailto:${m.email}`, Icon: Mail, label: "E-mail" },
  ].filter(Boolean);

export default function TeamGrid({ members = [] }) {
  const { loc } = useI18n();
  const list = members.filter((m) => m.visible !== false);

  if (!list.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="team-grid">
      {list.map((m, i) => (
        <Reveal key={`${m.name}-${i}`} delay={i * 90}>
          <article
            className="group h-full bg-white border border-[#E2E0D8] hover:border-[#C8B29B] transition-colors duration-500"
            data-testid={`team-member-${i}`}
          >
            <div className="media aspect-[4/5] overflow-hidden bg-[#EFECE6]">
              <SmartImage
                src={assetUrl(m.image)}
                alt={m.name}
                params="w=700&q=82&auto=format&fit=crop"
                className="w-full h-full object-cover grayscale-[12%] group-hover:grayscale-0 transition-[filter,transform] duration-700 group-hover:scale-[1.02]"
              />
            </div>
            <div className="p-6 lg:p-7">
              <h3 className="display text-[22px] lg:text-[26px] text-[#141414] leading-tight">{m.name}</h3>
              <p className="mono text-[9px] text-[#8C7862] mt-2.5">{loc(m.role)}</p>
              {loc(m.bio) && (
                <p className="text-[13.5px] text-[#141414]/70 mt-4 leading-relaxed">{loc(m.bio)}</p>
              )}
              {socialsOf(m).length > 0 && (
                <div className="flex items-center gap-2.5 mt-6 pt-5 border-t border-[#E2E0D8]">
                  {socialsOf(m).map(({ id, href, Icon, label }) => (
                    <a
                      key={id}
                      href={href}
                      target={id === "email" ? undefined : "_blank"}
                      rel="noreferrer"
                      aria-label={`${m.name} ${label}`}
                      data-testid={`team-social-${id}-${i}`}
                      className="w-11 h-11 border border-[#E2E0D8] flex items-center justify-center text-[#141414] hover:bg-[#141414] hover:text-[#FAFAFA] hover:border-[#141414] active:scale-95 transition-all duration-300"
                    >
                      <Icon size={15} strokeWidth={1.6} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  );
}
